import { useState, useEffect, useRef } from 'react';
import { useForm } from '@mantine/form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Select,
  Grid,
  Text,
  Group,
  Badge,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Page, ProcessHeader, Modal } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import {
  useOcrPdfFileMutation,
  useCompareloanFromPdfFileMutation,
  useSaveMutation,
} from '@/services/api';
import routes from '@/routes';

import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import LottieAnimation from '@/components/LottieAnimation';
import { getMutationResult } from '@/helpers/query';
import { notifications } from '@mantine/notifications';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import { updateMortgageCycle } from '@/data/features/mortgageCycle/mortgageCycle';
import { IconFileTypePdf, IconAlertTriangle, IconSettings } from '@tabler/icons-react';
import scanLottie from '@/assets/images/scan.lottie.json';
import { BANKS } from '@/data/constants/banks';
import ProcessButton from '@/components/ProcessButton';
import CustomFileInput from '@/components/CustomFileInput';
import PDFPageSelector from '@/components/PDFPageSelector';
import DocumentIcon from '@/assets/images/document.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './uploadBankFile.module.css';

const UploadBankFile = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '' });
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [selectedPages, setSelectedPages] = useState([]);
  const [originalFileForEditing, setOriginalFileForEditing] = useState(null);
  const mortgageCycleDetails = useSelector((state) => state.mortgageCycle);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const proposal = useSelector((state) => state.proposal);
  const { extraDetails, id } = proposal;
  const { cycleMortgage, isRecycling, loanTypeName } = extraDetails || {};

  // This is the case where the client has an existing loan and wants to take an additional loan
  const isRecyclingLoan = isRecycling === 'yes';

  const [data, setData] = useState(cycleMortgage?.currentLoans || []);

  const [apiContant] = useOcrPdfFileMutation();
  const [apiCompare] = useCompareloanFromPdfFileMutation();
  const [apiSave] = useSaveMutation();

  useIdInStoreGuard();

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      bank: mortgageCycleDetails?.bank || '',
      file: null,
    },
  });

  // Get selected bank data
  const getSelectedBankData = () => {
    if (!form.values.bank) return null;
    return BANKS.find((bank) => bank.value === form.values.bank);
  };

  // Initialize data with currentLoans from store if available
  useEffect(() => {
    if (mortgageCycleDetails?.currentLoans?.length > 0 && data.length === 0) {
      setData(mortgageCycleDetails.currentLoans);
    }
  }, [mortgageCycleDetails?.currentLoans, data.length]);

  const handleReturn = () => {
    if (isRecyclingLoan) {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'incomeLiabilities')?.path, { id }));
      return;
    }
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'bankingConduct')?.path, { id }));
  };

  const requiresPageSelection = form.values.bank === 'leumi';

  const handleFileChange = (file) => {
    if (!file) {
      form.setFieldValue('file', null);
      setOriginalFileForEditing(null);
      setSelectedPages([]);
      return;
    }

    if (requiresPageSelection) {
      setOriginalFileForEditing(file);
      form.setFieldValue('file', null);
      setSelectedPages([]);
      setShowPageSelector(true);
    } else {
      form.setFieldValue('file', file);
      setOriginalFileForEditing(null);
      setSelectedPages([]);
    }
  };

  const handlePagesSelected = (newFile, pages) => {
    form.setFieldValue('file', newFile);
    setSelectedPages(pages);
    setShowPageSelector(false);

    notifications.show({
      title: t('pages.mortgageCycle.bankFileUpload.pagesSelected.title'),
      message: t('pages.mortgageCycle.bankFileUpload.pagesSelected.message', { count: pages.length }),
      color: 'green',
    });
  };

  const handlePageSelectorClose = (cancelled = false) => {
    setShowPageSelector(false);
    // Only clear selectedPages and original file for editing if user cancelled
    if (cancelled) {
      setSelectedPages([]);
      form.setFieldValue('file', null);
      setOriginalFileForEditing(null);
    }
    // Don't clear originalFileForEditing if user confirmed - keep it for future editing
  };

  const handleSubmit = async () => {
    if (!form.values.bank || !form.values.file) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('אנא בחר בנק וצרף קובץ'),
      });
      return;
    }

    setLoading(true);
    function parseDateIsraeliFormat(dateStr) {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day); // monthIndex starts from 0
    }

    try {
      // First, scan the file to extract loan data
      const formData = new FormData();
      formData.append('bankName', form.values.bank);
      formData.append('pdf', form.values.file);
      formData.append('offerId', id);

      const loanData = await getMutationResult(formData, apiContant);
      const data = loanData.data.data || [];

      const today = new Date();

      const extractedLoans = data.filter((loan) => {
        const endDate = parseDateIsraeliFormat(loan.endDate);
        return endDate > today;
      });

      if (!extractedLoans.length) {
        setErrorModalData({
          title: t('pages.mortgageCycle.bankFileUpload.error.pdfReadFailed.title'),
          message: t('pages.mortgageCycle.bankFileUpload.error.pdfReadFailed.message'),
        });
        setShowErrorModal(true);
        setLoading(false);
        return;
      }

      // Update local state with extracted loans
      setData(extractedLoans);

      let rateType = 'cycle';

      // this is only for loans that can be recycled
      const loanRate = {
        replacementProperty: 'regular',
        renovationOrExpansion: 'cycle',
        anyPurposeWithPropertyCollateral: 'cycle',
      };

      // This is only for loans that can be recycled and have a specific purpose;
      // we want to cycle only those purposes
      const loanTocycle = {
        replacementProperty: 'דיור',
        renovationOrExpansion: 'דיור',
      };

      if (isRecyclingLoan) {
        rateType = loanRate[loanTypeName];
      }

      // Then, compare the loans
      const result = await getMutationResult({ loans: extractedLoans, rateType }, apiCompare);
      let { loans } = result.data;

      if (isRecyclingLoan) {
        const loanPorposeToCycle = loanTocycle[loanTypeName];

        if (loanPorposeToCycle) {
          loans = loans.map((loan) => {
            const { loanPurpose, isBetterToChange } = loan;
            const shouldCycle = loanPurpose === loanPorposeToCycle && isBetterToChange;

            return {
              ...loan,
              isBetterToChange: shouldCycle,
            };
          });
        }
      }

      const updatedLoans = loans.map(
        (loan) => ({
          ...loan,
          originalMonthsLeft: loan.monthsLeft,
        }),
      );

      // Save to database
      await apiSave({ id, cycleMortgage: { loans: updatedLoans } });

      // Update Redux store
      dispatch(updateMortgageCycle(
        {
          ...mortgageCycleDetails,
          comparisonData: updatedLoans,
          currentLoans: extractedLoans,
          bank: form.values.bank,
        },
      ));

      dispatch(updateExtraDetails(
        { cycleMortgage: { ...cycleMortgage, currentLoans: extractedLoans } },
      ));

      // Show success notification
      notifications.show({
        title: t('הקובץ נסרק בהצלחה'),
        message: t('הנתונים נשמרו ומוכנים להמשך'),
        color: 'green',
      });

      // Navigate to mortgage cycle action page with the loan data
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'loansComparation').path, { id }));
    } catch (error) {
      setErrorModalData({
        title: t('pages.mortgageCycle.bankFileUpload.error.pdfProcessingFailed.title'),
        message: t('pages.mortgageCycle.bankFileUpload.error.pdfProcessingFailed.message'),
      });
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <div className={classes.loading}>
          <LottieAnimation lottie={scanLottie} height="450px" width="450px" autoplay={true} loop={true} />
        </div>
      ) : (
        <ProcessHeader
          headerText={t('pages.mortgageCycle.bankFileUpload.title')}
          headerColor="#FFF6D1"
          headerTextColor="#DB6E00"
          subtitle={t('pages.mortgageCycle.bankFileUpload.subtitle')}
          showReturnButton
          onReturn={handleReturn}
          icon={DocumentIcon}
        >
          <Page.Section>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label={t('pages.mortgageCycle.bankFileUpload.bankSelect.label')}
                  data={BANKS.map((bank) => ({ value: bank.value, label: bank.name }))}
                  value={form.values.bank}
                  onChange={(value) => form.setFieldValue('bank', value)}
                  required
                  mb="md"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CustomFileInput
                  ref={fileInputRef}
                  label={t('pages.mortgageCycle.bankFileUpload.fileUpload.label')}
                  placeholder={t('pages.mortgageCycle.bankFileUpload.fileUpload.placeholder')}
                  accept=".pdf"
                  value={form.values.file}
                  onChange={handleFileChange}
                  helperText={t('pages.mortgageCycle.bankFileUpload.fileUpload.supportedFormats')}
                />
              </Grid.Col>
            </Grid>

            {/* Selected Pages Information - More Understandable */}
            {selectedPages.length > 0 && (
              <Group
                justify="space-between"
                align="center"
                p="md"
                bg="blue.0"
                style={{
                  borderRadius: 'var(--mantine-radius-md)',
                  border: '1px solid var(--mantine-color-blue-3)',
                }}
                mt="md"
              >
                <Group gap="md">
                  <Group gap="xs">
                    <Badge color="blue" variant="filled" size="md">
                      {selectedPages.length} עמודים נבחרו
                    </Badge>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" fw={500} c="blue.7">
                      עמודים:
                    </Text>
                    <Text size="sm" c="blue.6">
                      {selectedPages.sort((a, b) => a - b).join(', ')}
                    </Text>
                  </Group>
                </Group>
                <Button
                  variant="light"
                  size="sm"
                  color="blue"
                  leftSection={<IconSettings size={16} />}
                  onClick={() => setShowPageSelector(true)}
                >
                  ערוך בחירה
                </Button>
              </Group>
            )}
          </Page.Section>

          <Page.Section
            withCollapse
            withBackground
            startClose
            noPadding
            onCollapseChange={() => { }}
            title={t('pages.mortgageCycle.bankFileUpload.sectionTitle')}
          >

            {/* Bank-Specific Information - When Bank is Selected */}
            {form.values.bank && getSelectedBankData() && (
              <div className={classes.bankInfoContainer}>
                <Text className={classes.bankInfoHeader} mb="lg" size="lg" fw={600} c="#495057">
                  {t('pages.mortgageCycle.bankFileUpload.bankInfoTitle', { bankName: getSelectedBankData().name })}
                </Text>
                {/* First Row - Website, Phone, Working Hours */}
                <div className={classes.bankInfoRow}>
                  {getSelectedBankData().website && (
                    <div className={`${classes.bankInfoCard} ${classes.bankInfoCardWebsite}`}>
                      <Text className={classes.bankInfoLabel} mb="xs" size="sm" c="dimmed" fw={500}>
                        🌐 {t('pages.mortgageCycle.bankFileUpload.bankWebsite')}
                      </Text>
                      <a
                        href={getSelectedBankData().website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.bankInfoLink}
                      >
                        {getSelectedBankData().website}
                      </a>
                    </div>
                  )}

                  {getSelectedBankData().phone && (
                    <div className={`${classes.bankInfoCard} ${classes.bankInfoCardPhone}`}>
                      <Text className={classes.bankInfoLabel} mb="xs" size="sm" c="dimmed" fw={500}>
                        📞 {t('pages.mortgageCycle.bankFileUpload.bankPhone')}
                      </Text>
                      <Text className={classes.bankInfoContent} size="md" fw={600} c="#495057">
                        {getSelectedBankData().phone}
                      </Text>
                    </div>
                  )}

                  {getSelectedBankData().workingHours && (
                    <div className={`${classes.bankInfoCard} ${classes.bankInfoCardHours}`}>
                      <Text className={classes.bankInfoLabel} mb="xs" size="sm" c="dimmed" fw={500}>
                        🕒 {t('pages.mortgageCycle.bankFileUpload.bankWorkingHours')}
                      </Text>
                      <Text className={classes.bankInfoContent} size="md" c="#495057">
                        {getSelectedBankData().workingHours}
                      </Text>
                    </div>
                  )}
                </div>

                {getSelectedBankData().instructions && (
                  <div className={classes.instructionsContainer}>
                    <Text className={classes.instructionsTitle} mb="md" size="md" fw={600} c="#495057">
                      📋 {t('pages.mortgageCycle.bankFileUpload.bankInstructions')}
                    </Text>
                    <ol className={classes.instructionsList}>
                      {getSelectedBankData().instructions.map((instruction, index) => (
                        <li key={index} className={classes.instructionsListItem}>
                          {instruction}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}

            {/* General PDF Download - Always Available */}
            <div className={classes.pdfDownloadContainer}>
              <Text className={classes.pdfDownloadLabel} size="sm" c="dimmed">
                {t('pages.mortgageCycle.bankFileUpload.downloadFile')}
              </Text>
              <a
                href="/docs/מחזור-הנחיות דוח יתרות לסילוק קרדיט פרו.pdf"
                download
                target="_blank"
                rel="noopener noreferrer"
                className={classes.pdfDownloadButton}
              >
                <IconFileTypePdf size={18} />
                {t('pages.mortgageCycle.bankFileUpload.downloadButton')}
              </a>
            </div>

          </Page.Section>

          <Page.Actions>
            <ProcessButton
              onClick={handleSubmit}
              disabled={!form.values.bank || !form.values.file}
            />
          </Page.Actions>

          {/* Error Modal for PDF reading failures */}
          <Modal
            opened={showErrorModal}
            onClose={() => setShowErrorModal(false)}
            title={
              <Group gap="xs" align="center">
                <IconAlertTriangle size={24} color="#E91E63" />
                <Text fw={600} size="lg" c="#E91E63">
                  {errorModalData.title}
                </Text>
              </Group>
            }
            centered
            size="lg"
            showClose={false}
            disableClose={true}
            styles={{
              title: {
                borderBottom: '2px solid #FFE6F0',
                paddingBottom: 'var(--mantine-spacing-md)',
                marginBottom: 'var(--mantine-spacing-lg)',
              },
            }}
          >
            <Group gap="md" align="flex-start" mb="xl">
              <div>
                <Text size="md" fw={500} mb="xs" c="#333">
                  {errorModalData.message}
                </Text>
                <Text size="sm" c="#666" lh={1.6}>
                  <br />
                  <br />
                  <strong>{t('pages.mortgageCycle.bankFileUpload.error.recommendations')}:</strong>
                  <br />
                  • {t('pages.mortgageCycle.bankFileUpload.error.recommendation1')}
                  <br />
                  • {t('pages.mortgageCycle.bankFileUpload.error.recommendation2')}
                  <br />
                  • {t('pages.mortgageCycle.bankFileUpload.error.recommendation3')}
                </Text>
              </div>
            </Group>

            <Group justify="center" mt="xl">
              <Button
                variant="filled"
                color="pink"
                size="md"
                onClick={() => setShowErrorModal(false)}
                styles={{
                  root: {
                    backgroundColor: '#E91E63',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#C2185B',
                    },
                  },
                }}
              >
                {t('pages.mortgageCycle.bankFileUpload.error.understandButton')}
              </Button>
            </Group>
          </Modal>

          {/* PDF Page Selector Modal */}
          <PDFPageSelector
            opened={showPageSelector}
            onClose={handlePageSelectorClose}
            file={originalFileForEditing}
            onPagesSelected={handlePagesSelected}
            initialSelectedPages={selectedPages}
          />
        </ProcessHeader>
      )}
    </>
  );
};

export default UploadBankFile;
